import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import VisibilityIcon from '@mui/icons-material/Visibility';

const AsyncProgressOverview = ({ items, totalVoters }) => {
  // Calculate statistics
  const stats = items.reduce(
    (acc, item) => {
      if (item.status === 'estimated') {
        acc.estimated++;
      } else if (item.status === 'revealed') {
        acc.revealed++;
      } else if (item.isComplete) {
        acc.votingComplete++;
      } else {
        acc.inProgress++;
      }
      return acc;
    },
    { estimated: 0, revealed: 0, votingComplete: 0, inProgress: 0 }
  );

  const totalItems = items.length;
  const completedItems = stats.estimated + stats.revealed;
  const completionPercentage = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Session Progress
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Overall Completion
          </Typography>
          <Typography variant="body2" fontWeight="medium">
            {completedItems} / {totalItems} items
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={completionPercentage}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: '#2a2a2a',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              bgcolor: 'success.main'
            }
          }}
        />
      </Box>

      <Grid container spacing={1}>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center', p: 1 }}>
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 32 }} />
            <Typography variant="h5" fontWeight="bold">
              {stats.estimated}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Estimated
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center', p: 1 }}>
            <VisibilityIcon sx={{ color: 'info.main', fontSize: 32 }} />
            <Typography variant="h5" fontWeight="bold">
              {stats.revealed}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Revealed
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center', p: 1 }}>
            <Chip
              size="small"
              label="Ready"
              color="primary"
              sx={{ mb: 0.5 }}
            />
            <Typography variant="h5" fontWeight="bold">
              {stats.votingComplete}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Ready to Reveal
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center', p: 1 }}>
            <PendingIcon sx={{ color: 'warning.main', fontSize: 32 }} />
            <Typography variant="h5" fontWeight="bold">
              {stats.inProgress}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              In Progress
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {totalVoters > 0 && (
        <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {totalVoters} participant{totalVoters !== 1 ? 's' : ''} voting in this session
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default AsyncProgressOverview;
